import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  loginUser,
  clearError,
  sendForgotPasswordEmail,
  verifyPasscode,
  resetPassword,
  resetForgotPassword,
  clearForgotPasswordError,
} from '../../store/slices/authSlice';
import Loading from '../../components/Loading';
import AnnouncementDialogs from '../../components/AnnouncementDialogs';
import { useToast } from '../../components/Toast';
import './Login.css';
import logo from '../../assets/images/HAIA_logo.png';

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const { isLoading, isAuthenticated, user, error, forgotPassword } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    loginUser: '',
    password: '',
  });

  const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(
    searchParams.get('mode') === 'forgot'
  );
  const [forgotEmail, setForgotEmail] = useState('');
  const [passcode, setPasscode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (error && !isLoading) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, isLoading, toast, dispatch]);

  useEffect(() => {
    if (forgotPassword.error && !forgotPassword.isLoading) {
      toast.error(forgotPassword.error);
      dispatch(clearForgotPasswordError());
    }
  }, [forgotPassword.error, forgotPassword.isLoading, toast, dispatch]);

  useEffect(() => {
    if (isAuthenticated) {
      toast.success('ログインしました');
      const rawAccountType = user?.cmAccountType;
      const isFullAccess = rawAccountType === 'admin' || rawAccountType === 'ioyou';
      navigate(isFullAccess ? '/dashboard' : '/coupons');
    }
  }, [isAuthenticated, user, navigate, toast]);

  useEffect(() => {
    if (forgotPassword.isPasswordReset) {
      toast.success('パスワードが正常に変更されました');
      handleBackToLogin();
    }
  }, [forgotPassword.isPasswordReset, toast]);

  useEffect(() => {
    const mode = searchParams.get('mode');
    setIsForgotPasswordMode(mode === 'forgot');
  }, [searchParams]);

  useEffect(() => {
    return () => {
      dispatch(clearError());
      dispatch(resetForgotPassword());
    };
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    let normalizedValue = value;
    if (name === 'password' || name === 'loginUser') {
      normalizedValue = value.replace(/^\s+/, '');
    }

    setFormData((prev) => ({
      ...prev,
      [name]: normalizedValue,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(loginUser(formData));
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    setIsForgotPasswordMode(true);
    setSearchParams({ mode: 'forgot' });
    setFormData({
      loginUser: '',
      password: '',
    });
    setShowPassword(false);
    dispatch(clearForgotPasswordError());
  };

  const handleBackToLogin = (e) => {
    if (e) e.preventDefault();
    setIsForgotPasswordMode(false);
    setSearchParams({});
    setFormData({
      loginUser: '',
      password: '',
    });
    setShowPassword(false);
    setForgotEmail('');
    setPasscode('');
    setNewPassword('');
    setConfirmPassword('');
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    dispatch(resetForgotPassword());
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    dispatch(sendForgotPasswordEmail(forgotEmail));
  };

  const handleVerifyPasscode = async (e) => {
    e.preventDefault();
    if (!passcode) return;
    dispatch(verifyPasscode({ email: forgotEmail, passcode }));
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword || !confirmPassword) return;

    if (newPassword !== confirmPassword) {
      toast.error('パスワードが一致しません。');
      return;
    }

    const pwErrors = [];
    if (newPassword.length < 8) pwErrors.push('8文字以上');
    if (!/[A-Z]/.test(newPassword)) pwErrors.push('大文字1文字以上');
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(newPassword)) pwErrors.push('特殊文字1文字以上');
    if (pwErrors.length > 0) {
      toast.error('パスワードの要件: ' + pwErrors.join('、'));
      return;
    }

    dispatch(resetPassword({ email: forgotEmail, newPassword }));
  };

  const getLoadingText = () => {
    if (isLoading) return 'ログイン中...';
    if (!forgotPassword.isEmailSent) return 'メール送信中...';
    if (!forgotPassword.isPasscodeVerified) return 'パスコード確認中...';
    return 'パスワード変更中...';
  };

  return (
    <div className="login">
      {(isLoading || forgotPassword.isLoading) && <Loading fullScreen text={getLoadingText()} />}

      <div className="login__container">
        <div className="login__card">
          <div className="login__logo">
            <img src={logo} alt="logo" className="login__logo-img" />
          </div>

          {!isForgotPasswordMode ? (
            <form className="login__form" onSubmit={handleSubmit}>
              <div className="login__field">
                <label className="login__label" htmlFor="loginUser">
                  ログイン
                </label>
                <input
                  type="text"
                  id="loginUser"
                  name="loginUser"
                  className="login__input"
                  placeholder="ログインID"
                  value={formData.loginUser}
                  onChange={handleChange}
                  disabled={isLoading}
                />
              </div>

              <div className="login__field">
                <label className="login__label" htmlFor="password">
                  パスワード
                </label>
                <div className="login__input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    className="login__input login__input--password"
                    placeholder="パスワード"
                    value={formData.password}
                    onChange={handleChange}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="login__eye-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3.53 2.47a.75.75 0 0 0-1.06 1.06l18 18a.75.75 0 1 0 1.06-1.06l-18-18ZM22.676 12.553a11.249 11.249 0 0 1-2.631 4.31l-3.099-3.099a5.25 5.25 0 0 0-6.71-6.71L7.759 4.577a11.217 11.217 0 0 1 4.242-.827c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113Z" />
                        <path d="M15.75 12c0 .18-.013.357-.037.53l-4.244-4.243A3.75 3.75 0 0 1 15.75 12ZM12.53 15.713l-4.243-4.244a3.75 3.75 0 0 0 4.244 4.243Z" />
                        <path d="M6.75 12c0-.619.107-1.213.304-1.764l-3.1-3.1a11.25 11.25 0 0 0-2.63 4.31c-.12.362-.12.752 0 1.114 1.489 4.467 5.704 7.69 10.675 7.69 1.5 0 2.933-.294 4.242-.827l-2.477-2.477A5.25 5.25 0 0 1 6.75 12Z" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
                        <path fillRule="evenodd" d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113-1.487 4.471-5.705 7.697-10.677 7.697-4.97 0-9.186-3.223-10.675-7.69a1.762 1.762 0 0 1 0-1.113ZM17.25 12a5.25 5.25 0 1 1-10.5 0 5.25 5.25 0 0 1 10.5 0Z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="login__actions">
                <a
                  href="#forgot"
                  className="login__forgot-link"
                  onClick={handleForgotPassword}
                >
                  パスワードをお忘れですか
                </a>
                <button
                  type="submit"
                  className="login__button"
                  disabled={isLoading}
                >
                  ログイン
                </button>
              </div>
            </form>
          ) : (
            <form
              className="login__form"
              onSubmit={
                forgotPassword.isPasscodeVerified
                  ? handleResetPassword
                  : (forgotPassword.isEmailSent ? handleVerifyPasscode : handleSendEmail)
              }
            >
              <div className="login__field">
                <label className="login__label" htmlFor="forgotEmail">
                  メールアドレス
                </label>
                <input
                  type="email"
                  id="forgotEmail"
                  name="forgotEmail"
                  className="login__input"
                  placeholder="メールアドレスを入力"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value.replace(/^\s+/, ''))}
                  disabled={forgotPassword.isLoading || forgotPassword.isEmailSent}
                  required
                />
              </div>

              {forgotPassword.isEmailSent && !forgotPassword.isPasscodeVerified && (
                <div className="login__field">
                  <label className="login__label" htmlFor="passcode">
                    パスコード
                  </label>
                  <input
                    type="text"
                    id="passcode"
                    name="passcode"
                    className="login__input"
                    placeholder="パスコードを入力"
                    value={passcode}
                    onChange={(e) => setPasscode(e.target.value)}
                    disabled={forgotPassword.isLoading}
                    required
                  />
                </div>
              )}

              {forgotPassword.isPasscodeVerified && (
                <>
                  <div className="login__field">
                    <label className="login__label" htmlFor="newPassword">
                      新しいパスワード
                    </label>
                    <div className="login__input-wrapper">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        id="newPassword"
                        name="newPassword"
                        className="login__input login__input--password"
                        placeholder="新しいパスワードを入力"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value.replace(/^\s+/, ''))}
                        disabled={forgotPassword.isLoading}
                        required
                      />
                      <button
                        type="button"
                        className="login__eye-btn"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        tabIndex={-1}
                      >
                        {showNewPassword ? (
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M3.53 2.47a.75.75 0 0 0-1.06 1.06l18 18a.75.75 0 1 0 1.06-1.06l-18-18ZM22.676 12.553a11.249 11.249 0 0 1-2.631 4.31l-3.099-3.099a5.25 5.25 0 0 0-6.71-6.71L7.759 4.577a11.217 11.217 0 0 1 4.242-.827c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113Z" />
                            <path d="M15.75 12c0 .18-.013.357-.037.53l-4.244-4.243A3.75 3.75 0 0 1 15.75 12ZM12.53 15.713l-4.243-4.244a3.75 3.75 0 0 0 4.244 4.243Z" />
                            <path d="M6.75 12c0-.619.107-1.213.304-1.764l-3.1-3.1a11.25 11.25 0 0 0-2.63 4.31c-.12.362-.12.752 0 1.114 1.489 4.467 5.704 7.69 10.675 7.69 1.5 0 2.933-.294 4.242-.827l-2.477-2.477A5.25 5.25 0 0 1 6.75 12Z" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
                            <path fillRule="evenodd" d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113-1.487 4.471-5.705 7.697-10.677 7.697-4.97 0-9.186-3.223-10.675-7.69a1.762 1.762 0 0 1 0-1.113ZM17.25 12a5.25 5.25 0 1 1-10.5 0 5.25 5.25 0 0 1 10.5 0Z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="login__field">
                    <label className="login__label" htmlFor="confirmPassword">
                      パスワード確認
                    </label>
                    <div className="login__input-wrapper">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        name="confirmPassword"
                        className="login__input login__input--password"
                        placeholder="パスワードを再入力"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value.replace(/^\s+/, ''))}
                        disabled={forgotPassword.isLoading}
                        required
                      />
                      <button
                        type="button"
                        className="login__eye-btn"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        tabIndex={-1}
                      >
                        {showConfirmPassword ? (
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M3.53 2.47a.75.75 0 0 0-1.06 1.06l18 18a.75.75 0 1 0 1.06-1.06l-18-18ZM22.676 12.553a11.249 11.249 0 0 1-2.631 4.31l-3.099-3.099a5.25 5.25 0 0 0-6.71-6.71L7.759 4.577a11.217 11.217 0 0 1 4.242-.827c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113Z" />
                            <path d="M15.75 12c0 .18-.013.357-.037.53l-4.244-4.243A3.75 3.75 0 0 1 15.75 12ZM12.53 15.713l-4.243-4.244a3.75 3.75 0 0 0 4.244 4.243Z" />
                            <path d="M6.75 12c0-.619.107-1.213.304-1.764l-3.1-3.1a11.25 11.25 0 0 0-2.63 4.31c-.12.362-.12.752 0 1.114 1.489 4.467 5.704 7.69 10.675 7.69 1.5 0 2.933-.294 4.242-.827l-2.477-2.477A5.25 5.25 0 0 1 6.75 12Z" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
                            <path fillRule="evenodd" d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113-1.487 4.471-5.705 7.697-10.677 7.697-4.97 0-9.186-3.223-10.675-7.69a1.762 1.762 0 0 1 0-1.113ZM17.25 12a5.25 5.25 0 1 1-10.5 0 5.25 5.25 0 0 1 10.5 0Z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </>
              )}

              <div className="login__actions">
                <a
                  href="#login"
                  className="login__forgot-link"
                  onClick={handleBackToLogin}
                >
                  ログインに戻る
                </a>
                <button
                  type="submit"
                  className="login__button"
                  disabled={
                    forgotPassword.isLoading ||
                    (!forgotPassword.isEmailSent && !forgotEmail) ||
                    (forgotPassword.isEmailSent && !forgotPassword.isPasscodeVerified && !passcode) ||
                    (forgotPassword.isPasscodeVerified && (!newPassword || !confirmPassword))
                  }
                >
                  {forgotPassword.isLoading
                    ? '送信中...'
                    : (forgotPassword.isPasscodeVerified ? '変更' : (forgotPassword.isEmailSent ? '確認' : '送信'))
                  }
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      <AnnouncementDialogs />
    </div>
  );
};

export default Login;
